import type { Lead } from "@/types/lead";
import { ghostBtnClass, salesIndigoPanelClass } from "@/components/common/surfaceClasses";
import { openOpportunityWorkspace } from "@/lib/opportunity/openOpportunityWorkspace";
import { oppTodoUrgencyRowClass } from "@/lib/opportunity/oppTodoChecklistUrgency";
import { OPPORTUNITY_STATUS_LABELS } from "@/types/opportunity";
import { SalesConsoleCollapsibleSection } from "./SalesConsoleCollapsibleSection";
import { useSalesConsoleDealTodos } from "./useSalesConsoleDealTodos";

type Props = {
  selectedLead: Lead | null;
  onViewLead: (lead: Lead) => void;
};

export function SalesConsoleDealTodosSection({ selectedLead, onViewLead }: Props) {
  const { loading, error, opportunity, rows, reload } = useSalesConsoleDealTodos(selectedLead);
  const openCount = rows.length;

  return (
    <SalesConsoleCollapsibleSection
      title="Deal todos"
      subtitle="Post-sale checklist for the linked opportunity"
      defaultOpen={openCount > 0}
      fireflyTargetId="sc-deal-todos"
      headerRight={
        openCount > 0 ? (
          <span className="text-[10px] font-semibold rounded-full bg-amber-100 text-amber-950 dark:bg-amber-950/50 dark:text-amber-100 px-2 py-0.5">
            {openCount} open
          </span>
        ) : null
      }
    >
      <div className={`${salesIndigoPanelClass} bg-indigo-50/50 space-y-3`}>
        {selectedLead ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto text-xs text-gray-700 dark:text-zinc-300">
              Lead: <span className="font-medium">{selectedLead.name}</span>
            </span>
            <button
              type="button"
              className="text-xs text-blue-700 underline dark:text-blue-400"
              onClick={() => onViewLead(selectedLead)}
            >
              View lead
            </button>
            {opportunity ? (
              <button
                type="button"
                className="text-xs text-blue-700 underline dark:text-blue-400"
                onClick={() => openOpportunityWorkspace(opportunity.id, "todos")}
              >
                Open deal
              </button>
            ) : null}
          </div>
        ) : null}

        {!selectedLead ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Select a lead in Customer Calendar to see open post-sale steps for their linked opportunity.
          </p>
        ) : loading ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">Loading deal todos…</p>
        ) : error ? (
          <p className="text-xs text-red-700 dark:text-red-200">{error}</p>
        ) : !opportunity ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">No linked opportunity for this lead.</p>
        ) : !["ordered", "in_process"].includes(opportunity.status) ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Linked opportunity is{" "}
            {OPPORTUNITY_STATUS_LABELS[opportunity.status] ?? opportunity.status}. Deal todos appear when
            status is Ordered or In process.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-300">All post-sale steps are complete.</p>
        ) : (
          <ul className="space-y-2">
            {rows.map(({ item, urgency }) => (
              <li
                key={item.id}
                className={
                  "rounded-xl border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 " +
                  oppTodoUrgencyRowClass(urgency)
                }
              >
                <div className="font-medium">{item.label}</div>
                <p className="mt-0.5 text-[11px] text-gray-600 dark:text-zinc-300">
                  {item.due_date ? `Due ${item.due_date}` : "No due date"}
                </p>
              </li>
            ))}
          </ul>
        )}

        {selectedLead && opportunity ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={`${ghostBtnClass} px-2.5 py-1 text-[11px] font-semibold`}
              onClick={() => void reload()}
              disabled={loading}
            >
              Refresh
            </button>
            <button
              type="button"
              className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
              onClick={() => openOpportunityWorkspace(opportunity.id, "todos")}
            >
              Open todo list tab
            </button>
          </div>
        ) : null}
      </div>
    </SalesConsoleCollapsibleSection>
  );
}
